import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DataTable, EmptyState, PageHeader, useNotify } from '@routes-plan/ui';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { Office } from '../../../models/office';
import { LoanType } from '../../../models/loan-type';
import { useSelection } from '../../../components/context/SelectionContext';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { TextKey, useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, ContentCard, FilterRow, FormActions } from '../shared/AdminUi';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

/** Qué tipos de préstamo puede usar cada sede. */
const LoanTypeAssign: React.FC<ShowAlerts> = () => {
    const t = useT();
    const notify = useNotify();
    setPageTitle('loanTypes.assignTitle');
    const navigate = useNavigate();

    const [selectedLoanTypes, setSelectedLoanTypes] = useState<number[]>([]);
    const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [unselectedLoanTypes, setUnselectedLoanTypes] = useState<LoanType[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { office_id, setOffice_id } = useSelection();
    const { canAssign } = useResourcePermissions('/admin/loan-types');

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [loanTypesRes, allOffices] = await Promise.all([axios.get('loan-types'), axios.get('offices/all')]);
                setLoanTypes(Array.isArray(loanTypesRes.data?.data) ? loanTypesRes.data.data : []);
                setOffices(Array.isArray(allOffices.data?.data) ? allOffices.data.data : []);
            } catch (error) {
                notify.error(t('common.loadError'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!office_id) return;
        (async () => {
            try {
                const { data } = await axios.get(`offices/${office_id}/loan-types`);
                if (Array.isArray(data)) {
                    setSelectedLoanTypes(data.map((item) => item.loanTypeId));
                    setUnselectedLoanTypes([]);
                }
            } catch (error) {
                notify.error(t('common.loadError'));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [office_id]);

    const handleCheckboxChange = (loanTypeIdValue: number) => {
        const currentLoanType = loanTypes.find((loanType) => loanType.id === loanTypeIdValue);
        if (!currentLoanType) return;
        if (selectedLoanTypes.includes(loanTypeIdValue)) {
            setUnselectedLoanTypes((prev) => [...prev, currentLoanType]);
            setSelectedLoanTypes((prev) => prev.filter((id) => id !== loanTypeIdValue));
        } else {
            setUnselectedLoanTypes((prev) => prev.filter((loanType) => loanType.id !== loanTypeIdValue));
            setSelectedLoanTypes((prev) => [...prev, loanTypeIdValue]);
        }
    };

    const submit = async () => {
        if (!canAssign || !office_id) return;
        setSaving(true);
        try {
            await axios.post('/office-loan-types', {
                loanTypeId: selectedLoanTypes, // Tipos de préstamo seleccionados
                officeId: [office_id], // La sede va como arreglo
                unselectedLoanTypes: unselectedLoanTypes.map((loanType) => loanType.id), // Tipos que se desmarcaron
            });
            notify.success(t('common.savedOk'));
            navigate('/admin/loan-types');
        } catch (error) {
            notify.error(t('common.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const frequencyOf = (lt: LoanType) => {
        const id = Number(lt.loanTypeKind?.id);
        return [1, 2, 3, 4].includes(id) ? t(`frequency.${id}` as TextKey) : lt.loanTypeKind?.name ?? '';
    };

    return (
        <AdminPage>
            <PageHeader
                title={t('loanTypes.assignTitle')}
                description={t('loanTypes.assignSubtitle')}
                breadcrumbs={[{ label: t('loanTypes.title'), href: '#/admin/loan-types' }, { label: t('loanTypes.assignTitle') }]}
                helpAction={<ScreenTourButton />}
            />
            <ContentCard>
                <FilterRow sx={{ mb: 2 }}>
                    <TextField
                        id="assign-office"
                        data-tour="loan-type-assign-office"
                        select
                        size="small"
                        label={t('loanTypes.office')}
                        value={offices.length ? office_id ?? '' : ''}
                        onChange={(event) => setOffice_id(event.target.value)}
                        required
                        fullWidth
                    >
                        {offices.map((office: Office) => (
                            <MenuItem key={office.id} value={office.id}>
                                {office.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FilterRow>
                <DataTable<LoanType>
                    rows={loanTypes}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('loanTypes.assignTableLabel')}
                    tourId="loan-type-assign"
                    searchable
                    searchPlaceholder={t('loanTypes.searchPlaceholder')}
                    initialSort={{ columnId: 'name', direction: 'asc' }}
                    columns={[
                        {
                            id: 'select',
                            header: t('loanTypes.enabledInOffice'),
                            value: (r) => (selectedLoanTypes.includes(r.id) ? t('common.yes') : t('common.no')),
                            render: (r) => (
                                <Checkbox
                                    size="small"
                                    checked={selectedLoanTypes.includes(r.id)}
                                    onChange={() => handleCheckboxChange(r.id)}
                                    disabled={!office_id}
                                    inputProps={{ 'aria-label': `${t('common.select')} ${r.name}` }}
                                />
                            ),
                            width: 120,
                            align: 'center',
                        },
                        { id: 'name', header: t('common.name'), value: (r) => r.name, sortable: true },
                        { id: 'frequency', header: t('loanTypes.frequency'), value: (r) => frequencyOf(r), sortable: true },
                    ]}
                    emptyState={<EmptyState icon={<RequestQuoteOutlinedIcon />} title={t('loanTypes.assignEmpty')} dense />}
                />
            </ContentCard>
            <FormActions
                cancelTo="/admin/loan-types"
                submit={false}
                onSave={submit}
                saving={saving}
                allowed={canAssign}
                deniedTooltip={t('loanTypes.noPermissionAssign')}
                disabled={!office_id}
                saveLabel={t('loanTypes.saveAssignment')}
            />
        </AdminPage>
    );
};
export default LoanTypeAssign;
